import React, { useState, useEffect } from 'react'
import { Menu, X } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { MprolabIcon, MprolabSmall } from '../Svg'
import { Flex } from '../Flex'
import { CustomDrawer } from './CustomDrawer'
import NotificationBar from './NotificationBar'
import type { NavbarListingContract } from './ContractsSelector'
import { accessTokenLocalStorageKey } from '@mprolab/sdk'

const Wrapper = styled(Flex)<{ scrolled: boolean; isNotificationVisible: boolean; applyTransition: boolean }>`
  position: fixed;
  width: 100%;
  flex-direction: column;
  z-index: ${({ theme }) => theme.zIndices.navbar};
  margin-top: ${({ isNotificationVisible }) => (isNotificationVisible ? '48px' : '0')};
  transition: ${({ applyTransition }) => (applyTransition ? 'all 0.5s ease-in-out' : 'none')};
  background-color: ${({ scrolled, theme }) => (scrolled ? theme.colors.background : 'tranparent')};
  border-bottom: ${({ scrolled, theme }) => (scrolled ? `1px solid ${theme.colors.gray10}` : 'none')};
`

const MainWrapper = styled(Flex)`
  width: 100%;
  max-width: 1215px;
  height: 100%;
  margin: 0 auto;
  gap: 32px;
  padding: 16px;
  align-items: center;
  position: relative;
  ${({ theme }) => theme.mediaQueries.tablet} {
    gap: 16px;
    padding: 24px;
  }
`

const NavbarContent = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
`

const LogoWrapper = styled(Flex)`
  align-items: center;
  cursor: pointer;
  z-index: 11;
  margin-right: 54px;
  ${({ theme }) => theme.mediaQueries.tablet} {
    margin-right: 0;
  }
`

const HamburgerMenuWrapper = styled(Flex)`
  z-index: 11;
  cursor: pointer;
`

const NotificationBarWrapper = styled(Flex)`
  position: fixed;
  top: 0;
  width: 100%;
`

interface NavProps {
  isOpen: boolean
  onPresent: () => void
  onDismiss: () => void
  onLogoClick?: () => void
  navbarContent: React.ReactNode
  sidebarContent?: React.ReactNode
  contractsList: NavbarListingContract[]
  style?: React.CSSProperties
  account: string
}

const SESSION_STORAGE_KEY = 'notification'

const Navbar: React.FC<NavProps> = ({
  navbarContent,
  sidebarContent,
  contractsList,
  onLogoClick,
  isOpen,
  onPresent,
  onDismiss,
  style,
  account
}) => {
  const { colors, mediaQueries } = useTheme()
  const isTablet = useMediaQuery(mediaQueries.tablet)
  const isMobile = useMediaQuery(mediaQueries.mobile)
  const [scrolled, setScrolled] = useState(false)
  const [isNotificationVisible, setIsNotificationVisible] = useState(false)
  const [applyTransition, setApplyTransition] = useState(false)
  const localAccessToken = localStorage.getItem(accessTokenLocalStorageKey)

  const handleScroll = () => {
    const isScrolled = window.scrollY > 0
    setScrolled(isScrolled)
  }

  const closeNotification = () => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'false')
    setIsNotificationVisible(false)
  }

  useEffect(() => {
    const showNotification = sessionStorage.getItem(SESSION_STORAGE_KEY) !== 'false'
    setIsNotificationVisible(showNotification)
    // Delay of transition to avoid initial animation
    const delay = setTimeout(() => {
      setApplyTransition(true)
    }, 50)
    return () => clearTimeout(delay)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const displayNotification = !isEmpty(contractsList) && isNotificationVisible
  const displaySmallIcon = (account || localAccessToken) && isMobile

  return (
    <Wrapper {...{ scrolled, isNotificationVisible: displayNotification, applyTransition }}>
      <Flex flexDirection="column">
        {displayNotification && (
          <NotificationBarWrapper>
            <NotificationBar closeNotification={() => closeNotification()} {...{ contracts: contractsList }} />
          </NotificationBarWrapper>
        )}
        <MainWrapper style={style} {...{ scrolled }}>
          <LogoWrapper onClick={onLogoClick}>{displaySmallIcon ? <MprolabSmall /> : <MprolabIcon />}</LogoWrapper>
          <NavbarContent>{navbarContent}</NavbarContent>
          {isTablet && sidebarContent && (
            <HamburgerMenuWrapper>
              {isOpen ? (
                <X onClick={onDismiss} color={colors.white} />
              ) : (
                <Menu onClick={onPresent} color={colors.white} />
              )}
            </HamburgerMenuWrapper>
          )}
        </MainWrapper>
      </Flex>
      <CustomDrawer anchor="left" open={isOpen} onClose={onDismiss} onOpen={onPresent}>
        {sidebarContent}
      </CustomDrawer>
    </Wrapper>
  )
}

export default Navbar
